import { InfiniteData, QueryClient } from "@tanstack/react-query";
import { createQueryClientDefaultOptions } from "@flowstate/core";
import type { Todo } from "@/types/todo";

/**
 * TanStack Query client configuration for FlowState Start.
 * Mirrors the caching strategy from flowstate-automate.
 */
export const queryClient = new QueryClient({
  defaultOptions: createQueryClientDefaultOptions(),
});

/**
 * Centralized query keys — reuse these across hooks and server functions.
 */
export const queryKeys = {
  todos: {
    all: ["todos"] as const,
    lists: () => [...queryKeys.todos.all, "list"] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.todos.lists(), filters] as const,
    details: () => [...queryKeys.todos.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.todos.details(), id] as const,
    subtasks: (parentId: string) =>
      [...queryKeys.todos.all, "subtasks", parentId] as const,
  },
  users: {
    all: ["users"] as const,
    profile: (id: string) => [...queryKeys.users.all, "profile", id] as const,
  },
  stats: {
    all: ["stats"] as const,
    dashboard: () => [...queryKeys.stats.all, "dashboard"] as const,
  },
  // Track 14: Personal Dashboard
  dashboard: {
    all: ["dashboard"] as const,
    personal: (userId: string, organizationId?: string) =>
      ["dashboard", "personal", userId, organizationId ?? "none"] as const,
  },
  // Current organization (shared across all useCurrentOrganization() callers)
  currentOrganization: {
    all: ["currentOrganization"] as const,
    id: () => [...queryKeys.currentOrganization.all, "id"] as const,
  },
  clients: {
    all: ["clients"] as const,
    lists: () => [...queryKeys.clients.all, "list"] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.clients.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.clients.all, "detail", id] as const,
  },
  projects: {
    all: ["projects"] as const,
    lists: () => [...queryKeys.projects.all, "list"] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.projects.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.projects.all, "detail", id] as const,
  },
  accounting: {
    all: ["accounting"] as const,
    allTransactions: () => [...queryKeys.accounting.all, "transactions"] as const,
    transactions: (filters: Record<string, unknown>) =>
      [...queryKeys.accounting.allTransactions(), filters] as const,
    allAccounts: () => [...queryKeys.accounting.all, "accounts"] as const,
    accounts: (userId: string) =>
      [...queryKeys.accounting.allAccounts(), userId] as const,
  },
  commission: {
    all: ["commission"] as const,
    history: (userId: string) =>
      [...queryKeys.commission.all, "history", userId] as const,
  },
  // Track 3: Notifications
  notifications: {
    all: ["notifications"] as const,
    list: (userId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.notifications.all, "list", userId, ...(filters ? [filters] : [])] as const,
    unreadCount: (userId: string) =>
      [...queryKeys.notifications.all, "unread", userId] as const,
  },
  // Track 6: Tasks (complex CRUD + dependencies)
  tasks: {
    all: ["tasks"] as const,
    lists: () => [...queryKeys.tasks.all, "list"] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.tasks.lists(), filters] as const,
    details: () => [...queryKeys.tasks.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.tasks.details(), id] as const,
    subtasks: (parentId: string) =>
      [...queryKeys.tasks.all, "subtasks", parentId] as const,
  },
  // Track 8: AI Brain
  aiBrain: {
    all: ["aiBrain"] as const,
    chat: (userId: string) => [...queryKeys.aiBrain.all, "chat", userId] as const,
    quickActions: (userId: string, domain?: string) =>
      [...queryKeys.aiBrain.all, "quickActions", userId, domain ?? "all"] as const,
  },
  // Track 7: Time Management (time blocks)
  timeManagement: {
    all: ["time-management"] as const,
    lists: () => [...queryKeys.timeManagement.all, "list"] as const,
    list: (userId: string, allUsers?: boolean) =>
      [...queryKeys.timeManagement.lists(), userId, { allUsers }] as const,
    details: () => [...queryKeys.timeManagement.all, "detail"] as const,
    detail: (id: string) =>
      [...queryKeys.timeManagement.details(), id] as const,
  },
  // Track 9: Xero/CRM Sync
  xero: {
    all: ["xero"] as const,
    syncStatus: (userId: string) =>
      [...queryKeys.xero.all, "syncStatus", userId] as const,
    syncLog: (userId: string, limit?: number) =>
      [...queryKeys.xero.all, "syncLog", userId, limit] as const,
    syncConflicts: (userId: string, limit?: number) =>
      [...queryKeys.xero.all, "syncConflicts", userId, limit] as const,
    data: (userId: string) => [...queryKeys.xero.all, "data", userId] as const,
  },
  // Track 10: Sales Portal (commissions, analytics)
  salesPortal: {
    all: ["salesPortal"] as const,
    teams: (userId: string) =>
      [...queryKeys.salesPortal.all, "teams", userId] as const,
    analytics: (userId: string, teamId?: string) =>
      [...queryKeys.salesPortal.all, "analytics", userId, teamId] as const,
    commissionHistory: (userId: string) =>
      [...queryKeys.salesPortal.all, "commissionHistory", userId] as const,
    transactions: (userId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.salesPortal.all, "transactions", userId, ...(filters ? [filters] : [])] as const,
  },
  // Track 11: Gamification
  gamification: {
    all: ["gamification"] as const,
    stats: (userId: string) => [...queryKeys.gamification.all, "stats", userId] as const,
    achievements: (userId: string) => [...queryKeys.gamification.all, "achievements", userId] as const,
    leaderboard: (period: string, limit?: number) =>
      [...queryKeys.gamification.all, "leaderboard", period, limit ?? 20] as const,
  },
  // Track 12: Community (posts, comments)
  community: {
    all: ["community"] as const,
    posts: {
      all: () => [...queryKeys.community.all, "posts"] as const,
      list: (params: { limit: number; page: number }) =>
        [...queryKeys.community.posts.all(), "list", params] as const,
      detail: (postId: string) =>
        [...queryKeys.community.posts.all(), "detail", postId] as const,
      userList: (userId: string) =>
        [...queryKeys.community.posts.all(), "user", userId] as const,
    },
    comments: {
      all: () => [...queryKeys.community.all, "comments"] as const,
      list: (postId: string) =>
        [...queryKeys.community.comments.all(), postId] as const,
    },
  },
  // Track 9: CRM (client management)
  crm: {
    all: ["crm"] as const,
    statistics: (userId: string) => [...queryKeys.crm.all, "statistics", userId] as const,
    clients: (userId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.crm.all, "clients", userId, ...(filters ? [filters] : [])] as const,
    client: (userId: string, clientId: string) =>
      [...queryKeys.crm.all, "client", userId, clientId] as const,
    activities: (userId: string, clientId: string, limit?: number) =>
      [...queryKeys.crm.all, "activities", userId, clientId, limit] as const,
  },
  // Track 13: Audit Logs
  auditLogs: {
    all: ["auditLogs"] as const,
    list: (page: number, limit: number, action?: string) =>
      [...queryKeys.auditLogs.all, "list", page, limit, action] as const,
  },
  // Track 14: Documents & Attachments
  documents: {
    all: ["documents"] as const,
    stats: () => [...queryKeys.documents.all, "stats"] as const,
    list: (page: number, limit: number, search?: string) =>
      [...queryKeys.documents.all, "list", page, limit, search] as const,
  },
  // Track 15: Opportunities (CRM pipeline)
  opportunities: {
    all: ["opportunities"] as const,
    stats: () => [...queryKeys.opportunities.all, "stats"] as const,
    list: (page: number, limit: number, stage?: string) =>
      [...queryKeys.opportunities.all, "list", page, limit, stage] as const,
  },
  // Track 16: Archiving
  archiving: {
    all: ["archiving"] as const,
    stats: () => [...queryKeys.archiving.all, "stats"] as const,
  },
  // Track 17: Progress Reports
  progressReports: {
    all: ["progressReports"] as const,
    list: (page: number, limit: number, reportType?: string) =>
      [...queryKeys.progressReports.all, "list", page, limit, reportType] as const,
  },
  // Track 18: Health Coaching
  healthCoaching: {
    all: ["healthCoaching"] as const,
    overview: () => [...queryKeys.healthCoaching.all, "overview"] as const,
  },
  // Track 19: Subscriptions
  subscriptions: {
    all: ["subscriptions"] as const,
    stats: () => [...queryKeys.subscriptions.all, "stats"] as const,
    list: (page: number, limit: number, status?: string) =>
      [...queryKeys.subscriptions.all, "list", page, limit, status] as const,
  },
  // Track 20: Content Management
  contentManagement: {
    all: ["contentManagement"] as const,
    posts: (page: number, limit: number, search?: string) =>
      [...queryKeys.contentManagement.all, "posts", page, limit, search] as const,
  },
  // Track 21: Seasons (gamification)
  seasons: {
    all: ["seasons"] as const,
    rankings: (seasonId: string) =>
      [...queryKeys.seasons.all, "rankings", seasonId] as const,
  },
  // Partner Portal
  partners: {
    all: ["partners"] as const,
    lists: () => [...queryKeys.partners.all, "list"] as const,
    list: (userId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.partners.lists(), userId, ...(filters ? [filters] : [])] as const,
    detail: (partnerId: string) => [...queryKeys.partners.all, "detail", partnerId] as const,
    performance: (partnerId: string) => [...queryKeys.partners.all, "performance", partnerId] as const,
  },
} as const;

/**
 * Org-scoped cache configuration — extended GC time so switching orgs
 * doesn't evict the previous org's data.
 *
 * @see docs/TANSTACK_VS_ELECTRIC.md — stale-while-revalidate strategy
 */
export const ORG_SCOPED_CACHE_GC_TIME_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Default stale times per data category (Phase 4).
 *
 * @see docs/project/TANSTACK_START_MIGRATION_SOP.md — Dashboard Stale Time Tuning
 */
export const STALE_TIMES = {
  /** Frequently changing user data: todos, tasks */
  realtime: 30_000, // 30 seconds
  /** Moderate: time tracking, notifications */
  moderate: 60_000, // 1 minute
  /** Low urgency aggregates: team metrics, gamification */
  aggregate: 120_000, // 2 minutes
  /** System stats, profiles — rarely change */
  system: 5 * 60_000, // 5 minutes
} as const;

interface TodoListPage {
  todos: Todo[];
  nextCursor?: number;
  hasMore: boolean;
  totalCount: number;
}

type TodoListData = Todo[] | InfiniteData<TodoListPage>;

function isInfiniteTodoData(data: unknown): data is InfiniteData<TodoListPage> {
  if (typeof data !== "object" || data === null || !("pages" in data)) {
    return false;
  }

  const pages = (data as Record<string, unknown>).pages;
  if (!Array.isArray(pages)) {
    return false;
  }

  return pages.every(
    (page) =>
      typeof page === "object" &&
      page !== null &&
      "todos" in page &&
      Array.isArray((page as Record<string, unknown>).todos)
  );
}

function updateMatchingTodoLists(updater: (data: TodoListData) => TodoListData | undefined) {
  const entries = queryClient.getQueriesData<TodoListData | undefined>({
    queryKey: queryKeys.todos.lists(),
  });

  for (const [queryKey, data] of entries) {
    if (data === undefined) {
      continue;
    }

    const updated = updater(data);
    if (updated !== undefined) {
      queryClient.setQueryData(queryKey, updated);
    }
  }
}

/**
 * Optimistic cache helpers for TanStack Query todo lists.
 *
 * These helpers update both plain array list caches and paginated
 * `InfiniteData` caches used by `useInfiniteTodos`. Spreading a non-array
 * (e.g. `{ pages: [...] }`) caused "t is not iterable" when saving tasks.
 *
 * @deprecated Synced-entity mutations now write through TanStack DB
 * collections (`lib/db/collection-registry.ts`), which provide their own
 * optimistic layer + Electric convergence. These RQ-side optimistic writes
 * have no production callers; retained for the legacy `useInfiniteTodos`
 * read path only.
 */
export const optimisticUpdates = {
  todos: {
    addTodo(todo: Todo) {
      updateMatchingTodoLists((data) => {
        if (Array.isArray(data)) {
          return [...data, todo];
        }

        return {
          ...data,
          pages: data.pages.map((page, index) =>
            index === 0
              ? { ...page, todos: [...page.todos, todo] }
              : page
          ),
        };
      });
    },
    removeTodo(id: string) {
      updateMatchingTodoLists((data) => {
        if (Array.isArray(data)) {
          return data.filter((todo) => todo.id !== id);
        }

        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            todos: page.todos.filter((todo) => todo.id !== id),
          })),
        };
      });
    },
    updateTodo(id: string, updates: Partial<Todo>) {
      updateMatchingTodoLists((data) => {
        if (Array.isArray(data)) {
          return data.map((todo) =>
            todo.id === id ? { ...todo, ...updates } : todo
          );
        }

        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            todos: page.todos.map((todo) =>
              todo.id === id ? { ...todo, ...updates } : todo
            ),
          })),
        };
      });
    },
  },
};
